/* The colour cascade for per-event stages (U7, KTD-4/KTD-5).

   A stage colour is stored as a `--ak-*` token NAME (never a hex) so the
   contrast decision stays a single mechanism (KTD-4). tokens.css maps the
   seven built-in keys statically (`--stage-biber: var(--ak-biber)` …). A custom
   stage an event adds has NO static token — `var(--stage-eltern)` would be
   undefined and the chip would inherit whatever text colour happens to be
   there.

   So the dashboard sets `--stage-<key>: var(--<colour>)` from the registry on
   the root element for whichever event is selected. Applied once at the root,
   every `var(--stage-<key>)` in the subtree resolves — the Dashboard chips,
   the sheet accents, the Personen role chips, the Settings editor — and
   chalk.css keeps turning `--cc` into the Kreide border/fill. Built-in keys are
   re-set to the value tokens.css already gives them (inline wins, so the
   cascade is idempotent for them); a stage with no colour falls back to the
   secondary text colour, exactly like the old unknown-stage fallback.

   Text on a FILL is the other half of KTD-4. chalk.css hardcodes `#fff` on a
   light-mode fill and, because the curated tones are lightened in dark mode,
   hands the dark-mode fill a `#14222e` ink default that `--cc-on` can override
   (chalk.css:343-352). That default assumes the fill is light; a deep tone
   (the DPSG-red case) would read dark-on-dark. So the cascade also computes a
   theme-aware `--stage-on-<token>` for every palette colour — whichever of
   `#fff` or the ink contrasts more with the fill, by WCAG relative luminance —
   and a fill site supplies it as `--cc-on: var(--stage-on-<token>)`. */

import { useEffect } from 'react';
import { UNBEKANNT_KEY } from '../hooks/useStageRegistry.js';

/* The curated palette (KTD-4): the seven stage tones plus drachen/prev/oeko/
   stamm — eleven `--ak-*` tokens, and there is no free colour picker (R21). */
export const STAGE_PALETTE = [
  'ak-biber', 'ak-woe', 'ak-jupfi', 'ak-pfadi', 'ak-rover', 'ak-ausbildung',
  'ak-leitende', 'ak-drachen', 'ak-prev', 'ak-oeko', 'ak-stamm',
];

/* The two text colours a fill can carry, and their rgb. chalk.css picks
   between them; the helper below only ever chooses one of these two. */
const INK = '#14222e';
const WHITE = '#fff';
const INK_RGB = { r: 20, g: 34, b: 46 };
const WHITE_RGB = { r: 255, g: 255, b: 255 };

/* sRGB relative luminance, WCAG 2.x. */
function luminance({ r, g, b }) {
  const chan = (v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
}

/* WCAG contrast ratio between two luminances. */
function contrast(l1, l2) {
  const [a, b] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (a + 0.05) / (b + 0.05);
}

/* Resolve a CSS colour reference (e.g. `var(--ak-drachen)`) to rgb through a
   throwaway probe: getComputedStyle turns the cascade into an actual `rgb(…)`
   for the ACTIVE theme, so the same call answers light in light mode and dark
   in dark mode. Returns null off a browser (unit tests) or an unresolvable
   token, in which case chalk.css keeps its own default. */
function resolveRgb(ref) {
  if (typeof document === 'undefined' || !document.body) return null;
  const probe = document.createElement('span');
  probe.style.color = ref;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  document.body.removeChild(probe);
  const m = computed.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const parts = m[1].split(/[\s,]+/).slice(0, 3).map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [r, g, b] = parts;
  return { r, g, b };
}

/* Pick the text colour for a fill: white or the ink, whichever contrasts more.
   Returns a hex chalk.css reads through `--cc-on`; null when the token cannot
   be resolved so the stylesheet's own default applies instead. */
export function pickOnColor(ref) {
  const rgb = resolveRgb(ref);
  if (!rgb) return null;
  const L = luminance(rgb);
  return contrast(L, luminance(WHITE_RGB)) >= contrast(L, luminance(INK_RGB))
    ? WHITE
    : INK;
}

/* Pure: the inline `--stage-<key>` indirection for one subtree. The editor
   edits a local registry that may not be saved yet, so it needs its own copy
   of the cascade (the `--stage-on-*` decisions ride on <html> and cover every
   palette colour, so they are inherited from the root). */
export function stageVarsStyle(registry) {
  const style = {};
  for (const s of registry) {
    style[`--stage-${s.key}`] = s.colour ? `var(--${s.colour})` : 'var(--text-sub)';
  }
  // The rule-less fallback bucket still needs a colour when rendered.
  style[`--stage-${UNBEKANNT_KEY}`] = 'var(--text-sub)';
  return style;
}

/* The `--cc-on` a fill site supplies for its colour token (KTD-4). Undefined
   for a colour-less stage so chalk.css keeps its ink default. */
export function stageOnColorRef(colourToken) {
  return colourToken ? `var(--stage-on-${colourToken})` : undefined;
}

/* Resolve a stage key (or name) to its CSS colour reference. `--stage-*` is
   the single indirection every consumer already uses; the cascade guarantees
   it resolves for custom stages now. */
export function stageColorRef(key) {
  return key ? `var(--stage-${key})` : 'var(--text-sub)';
}

/* Apply the cascade to <html> for the selected event's registry, and drop the
   previous event's custom keys when the selection changes (a stale
   `--stage-<oldKey>` would otherwise linger and paint the next event). Also
   publishes a theme-aware `--stage-on-<token>` for the whole palette. Re-runs
   when the theme flips, because chalk.css keys its contrast off `data-theme`
   and the palette tokens resolve differently per mode. */
export function useStageColorVars(registry) {
  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const vars = stageVarsStyle(registry);
      for (const token of STAGE_PALETTE) {
        const on = pickOnColor(`var(--${token})`);
        if (on) vars[`--stage-on-${token}`] = on;
      }
      // Remove keys we set last time that this run no longer defines.
      for (const name of (useStageColorVars._applied ?? [])) {
        if (!(name in vars)) root.style.removeProperty(name);
      }
      for (const [name, value] of Object.entries(vars)) {
        root.style.setProperty(name, value);
      }
      useStageColorVars._applied = Object.keys(vars);
    };
    apply();
    // We write only the inline `style`, never `data-theme`, so our own writes
    // never retrigger this observer.
    const obs = new MutationObserver(apply);
    obs.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, [registry]);
}

export { UNBEKANNT_KEY };
